'use client';
import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import cartService from '../service/cartService';
import { UserContext } from './user-context';

interface CartContextType {
    cartCount: number;
    refreshCartCount: (data?: any) => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
    const { user } = useContext(UserContext) || {};
    const [cartCount, setCartCount] = useState<number>(0);

    const refreshCartCount = useCallback(async (data?: any) => {
        try {
            if (!user) {
                setCartCount(0);
                return;
            }
            const cartData = data || await cartService.getCart();
            // Sum quantities of all items (ignoring is_selected)
            const count = (cartData.items || []).reduce((sum: number, item: any) => sum + (item.quantity || 0) , 0);
            setCartCount(count);
        } catch (error) {
            console.error('Error refreshing cart count:', error);
            setCartCount(0);
        }
    }, [user]);

    useEffect(() => {
        refreshCartCount();
    }, [refreshCartCount, user?.id]);

    return (
        <CartContext.Provider value={{ cartCount, refreshCartCount }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (context === undefined) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
